import { AdminLocationMenu } from "@/admin/AdminLocationSelect.tsx";
import { CustomRadios, type FilterItem } from "@/admin/dashboard/CustomRadios.tsx";
import D from "@/admin/dashboard/Dashboard.module.css";
import { SmallDonut } from "@/admin/dashboard/DonutChart.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { WBarChart, type WBarChartOptions } from "@/admin/dashboard/WBarChart.tsx";
import { abbreviateNumber } from "@/admin/dashboard/utils.ts";
import { useTooltipPortal } from "@/admin/utils/useTooltipPortal.tsx";
import { adminAllStatisticsQueryAtom, type VmAdminStatisticsLoc } from "@/admin/vm/vmQuery.ts";
import { BadgeDot } from "@/components/Badge.tsx";
import { Separator } from "@/components/Separator.tsx";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { useThemeVar } from "@/modules/theme/useTheme.ts";
import { plural } from "@/utils/plural.tsx";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import type { BarSeriesOption } from "echarts/charts";
import { useAtomValue } from "jotai/react";
import { useState } from "react";
import { Label } from "react-aria-components";
import { entries, keys, sortBy, values } from "remeda";

type Distributed = "by_cpu" | "by_mem" | "by_os";
type Quantity = "quantity" | "volume";

const distributedItems: FilterItem<Distributed>[] = [
    { key: "by_cpu", title: "vCPU" },
    { key: "by_mem", title: "vRAM" },
    { key: "by_os", title: "OS" },
];

const stackedItems: FilterItem<Quantity>[] = [
    { key: "quantity", title: "Quantity" },
    { key: "volume", title: "Volume" },
];

function VirtualMachinesSectionContent() {
    const { data: vms } = useAtomValue(adminAllStatisticsQueryAtom);
    const locations = usePricingLocations();
    const defaultLocations = locations.map((item) => item.slug);
    const [checkedLocations, setCheckedLocations] = useState<string[]>(defaultLocations);
    const [distributed, setDistributed] = useState<Distributed>("by_cpu");
    const [stacked, setStacked] = useState<Quantity>("quantity");
    const colorWhite = useThemeVar("color-white");
    const colorMuted = useThemeVar("color-muted");
    const colorTextHoverAlpha = useThemeVar("color-text-hover-alpha");
    const colorText = useThemeVar("color-text");
    const colorPrimary = useThemeVar("color-primary");
    const colorPrimaryDark = useThemeVar("color-primary-dark");

    function mergeAndSum(arr: VmAdminStatisticsLoc[]) {
        const result: VmAdminStatisticsLoc = { by_cpu: {}, by_mem: {}, by_os: {}, location: "" };

        for (const entry of arr) {
            for (const category of ["by_cpu", "by_mem", "by_os"] as const) {
                for (const [key, value] of entries(entry[category])) {
                    result[category][key] ??= { running: 0, stopped: 0 };

                    result[category][key].running += value.running;
                    result[category][key].stopped += value.stopped;
                }
            }
        }

        return result;
    }

    const locationVms = mergeAndSum(vms.filter((item) => checkedLocations.includes(item.location)));

    let totalVMs = 0;
    let totalVMsRunning = 0;
    let totalVMsStopped = 0;

    for (const loc of vms) {
        for (const value of values(loc.by_cpu)) {
            if (value.running) {
                totalVMs += value.running;
                totalVMsRunning += value.running;
            }

            if (value.stopped) {
                totalVMs += value.stopped;
                totalVMsStopped += value.stopped;
            }
        }
    }

    const tooltip = (
        <div>
            <BadgeDot color="color-primary-dark">
                Stopped: <b>{totalVMsStopped.toFixed(0)}</b>
            </BadgeDot>

            <BadgeDot color="color-primary">
                Running: <b>{totalVMsRunning.toFixed(0)}</b>
            </BadgeDot>
        </div>
    );

    const vmItems = [
        { value: (totalVMsStopped / totalVMs) * 100, color: "var(--color-primary-dark)" },
        { value: (totalVMsRunning / totalVMs) * 100, color: "var(--color-primary)" },
    ];

    const distributedVms = locationVms ? locationVms[distributed] : {};
    const sortedItems = sortBy(keys(distributedVms), (l) => Number(l));

    const xAxisData = sortedItems.map((item): string => {
        switch (distributed) {
            case "by_cpu":
                return plural(Number(item), "vCPU", "vCPUs");
            case "by_mem":
                return `${item} GB`;
            case "by_os":
                return item;
            default:
                return exhaustiveSwitchCheck(distributed);
        }
    });

    const runningValues = sortedItems.map((item) =>
        stacked === "quantity" ? distributedVms[item].running : distributedVms[item].running * Number(item),
    );
    const stoppedValues = sortedItems.map((item) =>
        stacked === "quantity" ? distributedVms[item].stopped : distributedVms[item].stopped * Number(item),
    );
    const valueSuffix =
        stacked === "quantity" ? "pc" : distributed === "by_cpu" ? "vCPU" : distributed === "by_mem" ? "GB vRAM" : "";

    const { portal, formatter } = useTooltipPortal(({ name, dataIndex }) => {
        return (
            <div>
                <div>
                    <b>{name}</b>
                </div>

                {!!runningValues[dataIndex] && (
                    <BadgeDot color="color-primary">
                        Running: <b>{runningValues[dataIndex]}</b> {valueSuffix}
                    </BadgeDot>
                )}

                {!!stoppedValues[dataIndex] && (
                    <BadgeDot color="color-primary-dark">
                        Stopped: <b>{stoppedValues[dataIndex]}</b> {valueSuffix}
                    </BadgeDot>
                )}
            </div>
        );
    });

    const runningData: BarSeriesOption[] = [
        {
            name: "Running",
            type: "bar",
            stack: "total",
            itemStyle: {
                color: colorPrimary,
            },
            tooltip: { formatter },
            data: runningValues,
        },
    ];

    const stoppedData: BarSeriesOption[] = [
        {
            name: "Stopped",
            type: "bar",
            stack: "total",
            itemStyle: {
                color: colorPrimaryDark,
            },
            tooltip: { formatter },
            data: stoppedValues,
        },
    ];

    const options: WBarChartOptions = {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            borderWidth: 0,
            backgroundColor: colorWhite,
            textStyle: {
                color: colorText,
            },
            extraCssText: "box-shadow: 0 8px 20px rgba(0 0 0 / 10%);",
        },
        grid: {
            top: "5px",
            left: "5px",
            right: "5px",
            bottom: "5px",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: xAxisData,
            axisLabel: {
                rotate: 45,
            },
            axisLine: {
                lineStyle: {
                    color: colorMuted,
                },
            },
            axisTick: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: (value) => abbreviateNumber(value),
            },
            splitLine: {
                lineStyle: {
                    color: colorTextHoverAlpha,
                },
            },
        },
        series: [...runningData, ...stoppedData],
    };

    function changeDistributed(value: Distributed) {
        setDistributed(value);
        if (value === "by_os") {
            setStacked("quantity");
        }
    }

    function onChangeLocation(value: string[]) {
        setCheckedLocations(value);
    }

    return (
        <>
            {portal}

            <div className={D.ItemHeader}>
                <div>
                    <h2 className="font-size-heading">Virtual Machines</h2>
                    <p>Distribution by vCPU, vRAM and OS</p>
                </div>

                {!!totalVMs && (
                    <SmallDonut
                        marginLeft
                        title="Total number of VMs"
                        items={vmItems}
                        tooltip={tooltip}
                        total={totalVMs.toFixed(0)}
                    />
                )}
            </div>

            <Separator />

            <div className="flex justify-between pt-3 pb-3">
                <div className="flex gap-1rem font-size-small">
                    <BadgeDot color="color-chart-2">Running</BadgeDot>

                    <BadgeDot color="color-chart-3">Stopped</BadgeDot>
                </div>

                <AdminLocationMenu
                    value={checkedLocations}
                    locations={locations}
                    deletedLocations={[]}
                    onChange={onChangeLocation}
                />
            </div>

            <div className="flex items-center pb-4 justify-end">
                <Label className="font-size-small pr-1">Distributed by:</Label>
                <div className="flex items-center">
                    <CustomRadios items={distributedItems} value={distributed} onChange={changeDistributed} />
                </div>
            </div>

            <WBarChart options={options} />

            <div className="flex items-center">
                <Label className="font-size-small pr-1">Stack by:</Label>
                <div className="flex items-center">
                    <CustomRadios
                        disabled={distributed === "by_os"}
                        items={stackedItems}
                        value={stacked}
                        onChange={setStacked}
                    />
                </div>
            </div>
        </>
    );
}

export function VirtualMachinesSection() {
    return (
        <LightBlock loadingHeight="500px">
            <VirtualMachinesSectionContent />
        </LightBlock>
    );
}
